import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllCustomerSites } from '../../services/CustomerSiteServices';
import { getAllTax } from '../../services/TaxServices';
import {
    createDebitNote,
    getDebitNoteInvoiceContext,
    getDebitNoteNumber,
    getDebitNoteEinvoiceConfig,
    getInvoicesForDebitNote,
} from '../../services/DebitNoteServices';
import {
    applyDebitNoteTypeToLines,
    buildDebitNoteTotals,
    DEBIT_NOTE_TYPES,
    DEBIT_NOTE_VOUCHER_MODES,
    getDebitNoteTypeLabel,
    recalculateDebitNoteLine,
    syncInvoiceDebitAmounts,
} from './DebitNoteUtils';
import TableRowsDebitNote from './TableRowsDebitNote';
import { getCurrentDate } from '../../utils/DateUtils';
import InvoiceEinvoiceSection from '../../components/InvoiceEinvoiceSection';
import { shouldGenerateEinvoiceOnSave } from '../../utils/einvoiceHelpers';

function DebitNote() {
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({
        dn_date: getCurrentDate(),
        dn_type: 'rate_difference',
        voucher_mode: 'Item Invoice',
    });
    const [plants, setPlants] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [sites, setSites] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [taxMap, setTaxMap] = useState({});
    const [productLines, setProductLines] = useState([]);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
    const [edebitConfig, setEdebitConfig] = useState(null);

    const totals = useMemo(
        () => buildDebitNoteTotals(productLines, invoiceLines),
        [productLines, invoiceLines],
    );

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((plant) => plant.status).map((plant) => ({
                value: plant.id,
                label: plant.plant_alias,
            })));
        });
        getAllCustomerMain(cookies).then((response) => {
            setCustomers((response.customer_list || []).filter((customer) => customer.status).map((customer) => ({
                value: customer.id,
                label: customer.name,
            })));
        });
        getAllTax(cookies).then((response) => {
            const taxList = (response.tax_list || []).filter((tax) => tax.status);
            setTaxes(taxList.map((tax) => ({ value: tax.id, label: tax.name })));
            const map = {};
            taxList.forEach((tax) => {
                map[tax.id] = {
                    cgst_per: tax.tax_type === 'igst' ? 0 : tax.tax_value1,
                    sgst_per: tax.tax_type === 'igst' ? 0 : tax.tax_value2,
                    igst_per: tax.tax_type === 'igst' ? tax.tax_value1 : 0,
                };
            });
            setTaxMap(map);
        });
    }, [cookies]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.dn_date) {
            return;
        }
        const moment = require('moment');
        getDebitNoteNumber(cookies, inputs.plant_id, moment(inputs.dn_date).format('DD-MM-YYYY'))
            .then((response) => {
                setInputs((values) => ({
                    ...values,
                    prefix: response.prefix,
                    sl_no: response.dn_no,
                    dn_no: `${response.prefix}/${response.dn_no}`,
                }));
            })
            .catch(() => {});
    }, [cookies, inputs.plant_id, inputs.dn_date]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.customer_id) {
            setInvoices([]);
            return;
        }
        getInvoicesForDebitNote(cookies, inputs.plant_id, inputs.customer_id)
            .then((response) => {
                setInvoices((response.invoice_list || []).map((invoice) => ({
                    value: invoice.id,
                    label: `${invoice.invoice_no} (${invoice.invoice_date})`,
                    ...invoice,
                })));
            })
            .catch(() => setInvoices([]));
    }, [cookies, inputs.plant_id, inputs.customer_id]);

    useEffect(() => {
        if (!inputs.customer_id) {
            setSites([]);
            return;
        }
        const customerId = parseInt(inputs.customer_id, 10);
        getAllCustomerSites(cookies).then((response) => {
            setSites((response.consignee_list || [])
                .filter((site) => site.status && site.customer?.id === customerId)
                .map((site) => ({
                    value: site.id,
                    label: site.project_name || site.name,
                })));
        }).catch(() => setSites([]));
    }, [cookies, inputs.customer_id]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.dn_date || !inputs.invoice_id) {
            setEdebitConfig(null);
            return;
        }
        const moment = require('moment');
        getDebitNoteEinvoiceConfig(
            cookies,
            inputs.plant_id,
            moment(inputs.dn_date).format('DD-MM-YYYY'),
            totals.debit_amt,
            inputs.invoice_id,
        ).then((response) => setEdebitConfig(response))
            .catch(() => setEdebitConfig(null));
    }, [cookies, inputs.plant_id, inputs.dn_date, inputs.invoice_id, totals.debit_amt]);

    const loadInvoiceContext = (invoiceId) => {
        if (!invoiceId) {
            setProductLines([]);
            setInvoiceLines([]);
            return;
        }
        getDebitNoteInvoiceContext(cookies, invoiceId)
            .then((response) => {
                const nextProductLines = (response.product_lines || []).map((line, index) => recalculateDebitNoteLine({
                    ...line,
                    sl_no: index + 1,
                }, taxMap[line.tax_id] || {}));
                const nextInvoiceLines = response.invoice_lines || [];
                const typed = applyDebitNoteTypeToLines(inputs.dn_type, nextProductLines, nextInvoiceLines);
                setProductLines(typed.productLines);
                setInvoiceLines(typed.invoiceLines);
                setInputs((values) => ({
                    ...values,
                    consignee_id: response.invoice?.consignee_id || values.consignee_id,
                }));
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load invoice.' }, 'Load Failed');
            });
    };

    const onCustomerSelect = (selected) => {
        setSelectedCustomerOption(selected);
        setInputs((values) => ({
            ...values,
            customer_id: selected ? selected.value : '',
            consignee_id: '',
            invoice_id: '',
        }));
        setProductLines([]);
        setInvoiceLines([]);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((values) => ({ ...values, [name]: value }));
        if (name === 'invoice_id') {
            loadInvoiceContext(value);
        }
        if (name === 'dn_type' && inputs.invoice_id) {
            const typed = applyDebitNoteTypeToLines(value, productLines, invoiceLines);
            setProductLines(typed.productLines);
            setInvoiceLines(typed.invoiceLines);
        }
    };

    const handleProductLineChange = (index, name, value) => {
        const rows = [...productLines];
        rows[index] = recalculateDebitNoteLine({
            ...rows[index],
            [name]: value,
        }, name === 'tax_id' ? (taxMap[value] || {}) : (taxMap[rows[index].tax_id] || {}), name);
        setProductLines(rows);
        setInvoiceLines((lines) => syncInvoiceDebitAmounts(rows, lines));
    };

    const handleInvoiceLineChange = (index, value) => {
        const rows = [...invoiceLines];
        rows[index] = { ...rows[index], debit_amt: value };
        setInvoiceLines(rows);
    };

    const addProductRow = () => {
        setProductLines((rows) => {
            const nextRows = [...rows, recalculateDebitNoteLine({
                sl_no: rows.length + 1,
                description: '',
                unit: '',
                qty: 0,
                basic_rate: 0,
                taxable_amt: 0,
                tax_id: '',
            })];
            setInvoiceLines((lines) => syncInvoiceDebitAmounts(nextRows, lines));
            return nextRows;
        });
    };

    const removeProductRow = (index) => {
        setProductLines((rows) => {
            const nextRows = rows.filter((_, rowIndex) => rowIndex !== index);
            setInvoiceLines((lines) => syncInvoiceDebitAmounts(nextRows, lines));
            return nextRows;
        });
    };

    const buildPayload = () => {
        const moment = require('moment');
        return {
            plant_id: inputs.plant_id,
            customer_id: inputs.customer_id,
            consignee_id: inputs.consignee_id,
            invoice_id: inputs.invoice_id,
            dn_type: inputs.dn_type,
            voucher_mode: inputs.voucher_mode,
            dn_date: moment(inputs.dn_date).format('DD-MM-YYYY'),
            prefix: inputs.prefix,
            sl_no: inputs.sl_no,
            remarks: inputs.remarks || '',
            generate_edebit_note: shouldGenerateEinvoiceOnSave(edebitConfig),
            product_lines: productLines.map((line) => ({
                description: line.description,
                unit: line.unit,
                qty: line.qty,
                basic_rate: line.basic_rate,
                taxable_amt: line.taxable_amt,
                tax_id: line.tax_id,
            })),
            invoice_lines: invoiceLines.map((line) => ({
                invoice_id: line.invoice_id,
                debit_amt: line.debit_amt,
                remarks: line.remarks || '',
            })),
        };
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Do you want to save this Debit Note?',
            html: edebitConfig?.can_generate_einvoice
                ? '<p>E-Debit Note IRN (DBN) will be generated on the GST portal for this B2B debit note.</p>'
                : '',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            createDebitNote(cookies, buildPayload())
                .then((response) => {
                    const irnMsg = response.einvoice_irn
                        ? `<p style="margin-top:8px;">E-Debit Note IRN: <b>${response.einvoice_irn}</b></p>`
                        : '';
                    Swal.fire({
                        title: 'Saved!',
                        html: `${response.message || 'Debit Note saved.'}${irnMsg}`,
                        icon: 'success',
                    }).then(() => navigate(`/DebitNoteEdit/${response.id}/`));
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Save Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    const Reset = () => {
        setInputs({
            dn_date: getCurrentDate(),
            dn_type: 'rate_difference',
            voucher_mode: 'Item Invoice',
        });
        setSelectedCustomerOption(null);
        setSites([]);
        setProductLines([]);
        setInvoiceLines([]);
        setInvoices([]);
        setEdebitConfig(null);
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay inProgress={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls tableLink="/DebitNoteTable" onCancel={Reset} enableCancel={true} />
                    <FormPageLayout
                        moduleLabel="ACCOUNTS"
                        title="Create Debit Note"
                        subtitle="Issue debit notes for rate difference, additional charges, or other adjustments against invoices."
                        modeLabel={`${getDebitNoteTypeLabel(inputs.dn_type).toUpperCase()} CREATE MODE`}
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label htmlFor="plant_id" className="form-group col-sm-5 text-right">Plant Location</label>
                                        <select id="plant_id" name="plant_id" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.plant_id || ''}>
                                            <option value="">Select Plant</option>
                                            {plants.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select><br />

                                        <label htmlFor="customer_id" className="form-group col-sm-5 text-right">Billing Name</label>
                                        <div className="col-sm-6" style={{ paddingLeft: 0, paddingRight: 0 }}>
                                            <Select
                                                id="customer_id"
                                                inputId="customer_id"
                                                options={customers}
                                                isSearchable={true}
                                                value={selectedCustomerOption}
                                                onChange={onCustomerSelect}
                                                placeholder="Select Customer"
                                                className="mandatory-form-control react-select-dropdown"
                                            />
                                            <input type="hidden" name="customer_id" value={inputs.customer_id || ''} required />
                                        </div><br />

                                        <label htmlFor="consignee_id" className="form-group col-sm-5 text-right">Site Name</label>
                                        <select id="consignee_id" name="consignee_id" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.consignee_id || ''}>
                                            <option value="">Select Site</option>
                                            {sites.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select><br />

                                        <label htmlFor="invoice_id" className="form-group col-sm-5 text-right">Invoice No</label>
                                        <select id="invoice_id" name="invoice_id" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.invoice_id || ''}>
                                            <option value="">Select Invoice</option>
                                            {invoices.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select><br />

                                        <label htmlFor="dn_type" className="form-group col-sm-5 text-right">Debit Note Type</label>
                                        <select id="dn_type" name="dn_type" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.dn_type || ''}>
                                            {DEBIT_NOTE_TYPES.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select><br />

                                        <label htmlFor="voucher_mode" className="form-group col-sm-5 text-right">Voucher Mode</label>
                                        <select id="voucher_mode" name="voucher_mode" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.voucher_mode || ''}>
                                            {DEBIT_NOTE_VOUCHER_MODES.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </Col>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-4 text-right">Debit Note No.</label>
                                        <input type="text" className="form-control col-sm-6" value={inputs.dn_no || ''} readOnly style={{ backgroundColor: 'white', cursor: 'not-allowed' }} /><br />

                                        <label htmlFor="dn_date" className="form-group col-sm-4 text-right">Debit Note Date</label>
                                        <input type="date" id="dn_date" name="dn_date" className="form-control col-sm-6" required onChange={handleChange} value={inputs.dn_date || ''} />
                                    </div>
                                </Col>
                            </Row>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Action</th>
                                            <th>S.No</th>
                                            <th>Description</th>
                                            <th>Unit</th>
                                            <th>Qty</th>
                                            <th>Basic Rate</th>
                                            <th>Amount</th>
                                            <th>Tax %</th>
                                            <th>Tax Amount</th>
                                            <th>Total Amount</th>
                                        </tr>
                                    </thead>
                                    <TableRowsDebitNote
                                        rowsData={productLines}
                                        taxes={taxes}
                                        dnType={inputs.dn_type}
                                        onChange={handleProductLineChange}
                                        onAddRow={addProductRow}
                                        onRemoveRow={removeProductRow}
                                    />
                                </table>
                                <table className="table order-list" style={{ textAlign: 'center', backgroundColor: 'white' }}>
                                    <tbody>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Total Taxable Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.taxable_amt} /></td>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>CGST Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.cgst_amt} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Total GST Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.gst_amt} /></td>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>SGST Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.sgst_amt} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Total Gross Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.gross_amt} /></td>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>IGST Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.igst_amt} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Round off</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.round_off} /></td>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Total Debit Note Value</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.debit_amt} /></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Sl. No.</th>
                                            <th>Doc Type</th>
                                            <th>Plant Name</th>
                                            <th>Project/Site Name</th>
                                            <th>Invoice Number</th>
                                            <th>Invoice Date</th>
                                            <th>Invoice Amount</th>
                                            <th>Received Amount</th>
                                            <th>Balance Amount</th>
                                            <th>Debit Amount</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceLines.map((line, index) => (
                                            <tr key={`cn-inv-${index}`}>
                                                <td>{index + 1}</td>
                                                <td>{line.doc_type || 'Invoice'}</td>
                                                <td>{line.plant_name}</td>
                                                <td>{line.site_name}</td>
                                                <td>{line.invoice_no}</td>
                                                <td>{line.invoice_date}</td>
                                                <td>{line.invoice_amt}</td>
                                                <td>{line.received_amt}</td>
                                                <td>{line.balance_amt}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={line.debit_amt ?? ''}
                                                        onChange={(event) => handleInvoiceLineChange(index, event.target.value)}
                                                    />
                                                </td>
                                                <td>{line.remarks || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label htmlFor="remarks" className="form-group col-sm-2 text-right">Remarks</label>
                                <textarea rows="2" cols="50" className="form-control col-sm-10" id="remarks" name="remarks" onChange={handleChange} value={inputs.remarks || ''} />
                            </div>

                            <InvoiceEinvoiceSection config={edebitConfig} />
                        </div>

                        <div className="footer text-center">
                            <Button type="submit" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }}>Save</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={Reset}>Reset</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/DebitNoteTable')}>View</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}>Home</Button>
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default DebitNote;
